/**
 * SubscriptionPricingView Component
 * Main subscription pricing comparison page (embedded in Home view state)
 *
 * Pattern: Mobile-first vertical stack within max-w-md container
 * Design: All 3 tiers visible with billing toggle and expandable features
 */

import { useState } from 'react'
import { Container, Stack } from '@talktoast/ui'
import { SubscriptionTier, BillingInterval } from '@talktoast/shared/types/subscription'
import { BillingToggle } from './BillingToggle'
import { TierCard } from './TierCard'

interface SubscriptionPricingViewProps {
  onBack: () => void
  onSelectTier: (tier: 'premium', interval: BillingInterval) => void
  currentTier?: SubscriptionTier
}

export function SubscriptionPricingView({ onBack, onSelectTier, currentTier }: SubscriptionPricingViewProps) {
  const [interval, setInterval] = useState<BillingInterval>('yearly')

  return (
    <Container maxWidth="md" padding="md">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">
          Choose Your Plan
        </h1>
        <p className="text-sm text-gray-600 text-center">
          Start free or upgrade to unlock unlimited meal planning
        </p>
      </div>

      {/* Billing Toggle */}
      <BillingToggle value={interval} onChange={setInterval} savings={17} />

      {/* Pricing Cards - Vertical Stack */}
      <Stack direction="vertical" gap={3}>
        <TierCard
          tier="free"
          interval={interval}
          currentTier={currentTier}
          onSelect={onSelectTier}
        />

        <TierCard
          tier="premium"
          interval={interval}
          currentTier={currentTier}
          highlighted={true}
          onSelect={onSelectTier}
        />

      </Stack>

      {/* FAQ Section (Optional) */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h2 className="text-lg font-bold text-gray-900 mb-4 text-center">
          Frequently Asked Questions
        </h2>

        <div className="space-y-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-1 text-sm">
              What happens after my free trial?
            </h3>
            <p className="text-gray-600 text-xs">
              After your 7-day free trial, you'll be charged the subscription fee. You can cancel anytime during the trial with no charge.
            </p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-1 text-sm">
              Can I change my plan later?
            </h3>
            <p className="text-gray-600 text-xs">
              Yes! You can upgrade, downgrade, or cancel your subscription at any time through your account settings.
            </p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-1 text-sm">
              What payment methods do you accept?
            </h3>
            <p className="text-gray-600 text-xs">
              We accept all major credit cards, debit cards, and digital payment methods through our secure payment processor, Stripe.
            </p>
          </div>
        </div>
      </div>
    </Container>
  )
}
